import { useCallback, useEffect, useState } from "react";
import { Search, X } from "lucide-react";

import MapView from "../mapComponent";
import ListView from "./Maplist";
import api from "../interceptors";
import ViewToggle from "../mapListbutton/ToggleView";
import "./Maplist.css";

const MapListContainer = () => {
  const [activeTab, setActiveTab] = useState("mapView");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const savedTab = localStorage.getItem("activeTab");
    if (savedTab) {
      setActiveTab(savedTab);
    }
  }, []);

  const fetchSearchResults = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/search?query=${searchTerm}`);
      setSearchResults(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (searchTerm) {
        fetchSearchResults();
      } else {
        setSearchResults([]);
      }
    }, 400);

    return () => clearTimeout(delay);
  }, [searchTerm, fetchSearchResults]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem("activeTab", tab);
  };

  const getMatchedField = (loc, currentSearchTerm) => {
    const term = currentSearchTerm.toLowerCase();
    const chargerName = loc.charger_name || loc.station_name;

    if (chargerName?.toLowerCase().includes(term)) {
      return chargerName;
    }

    if (loc.station_id?.toLowerCase().includes(term)) {
      return loc.station_id;
    }
    if (loc.Address?.toLowerCase().includes(term)) {
      return loc.Address;
    }

    return chargerName || loc.station_id;
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchResults([]);
    setSelectedLocation(null);
    setShowDropdown(false);
  };

  return (
    <div
      className={`map-list-container ${
        activeTab === "mapView" ? "is-map-view" : "is-list-view"
      } relative flex h-full min-w-0 max-w-full flex-col overflow-hidden`}
    >
      <div className="map-toolbar">
        <div className="map-toolbar__top">
          <div className="map-toolbar__copy">
            <span>Station locator</span>
            <strong>{activeTab === "mapView" ? "Map view" : "List view"}</strong>
          </div>
          <ViewToggle activeTab={activeTab} setActiveTab={handleTabChange} />
        </div>

        <div className="map-search">
          <span className="map-search__icon">
            <Search size={16} />
          </span>

          <input
            className="map-search__input"
            placeholder="Search station, charger, address..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(true);
            }}
          />

          {searchTerm && (
            <button
              type="button"
              className="map-search__clear"
              aria-label="Clear search"
              onClick={clearSearch}
            >
              <X size={16} />
            </button>
          )}

          {showDropdown && searchTerm && (
            <ul className="map-search-results">
              {loading ? (
                <li className="map-search-results__state">
                  Loading...
                </li>
              ) : searchResults.length === 0 ? (
                <li className="map-search-results__state">
                  No locations found
                </li>
              ) : (
                searchResults.map((loc) => (
                  <li
                    key={loc.station_id}
                    className="map-search-result"
                    onClick={() => {
                      setSearchTerm(loc.charger_name || loc.station_id);
                      setSearchResults([]);
                      setShowDropdown(false);
                      setSelectedLocation(loc);
                    }}
                  >
                    <span className="map-search-result__title">
                      {getMatchedField(loc, searchTerm)}
                    </span>
                    <span className="map-search-result__meta">
                      {loc.station_id}
                      {loc.charger_name ? ` - ${loc.charger_name}` : ""}
                    </span>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </div>

      {activeTab === "mapView" && (
        <MapView
          searchTerm={searchTerm}
          searchResults={searchResults}
          setSearchTerm={setSearchTerm}
          selectedLocation={selectedLocation}
        />
      )}

      {activeTab === "listView" && (
        <ListView
          searchTerm={searchTerm}
          searchResults={searchResults}
          selectedLocation={selectedLocation}
        />
      )}
    </div>
  );
};

export default MapListContainer;
